import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { parseMoney, signedTransactionCents } from '../../api/parseMoney'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { AccountsError } from './useAccounts'

/**
 * `GET /transactions?account_id=` — the detail screen's "Transactions on
 * this account" panel, pulled forward from M6 by explicit user instruction
 * (the design gap-fill pass, 2026-09-17) rather than built to the original
 * plan's schedule; the field this panel reads (`account_id`) already exists
 * on the endpoint (`openapi.json`), so nothing on the backend moved.
 *
 * Shaped like `useMerchants.ts`'s `MerchantTransaction`/`listMerchantTransactions`
 * — same wire endpoint, same "no pager, a fixed preview size" scope, same
 * mapping discipline — with `ownerName` added (`effective_beneficial_owner`,
 * the transaction-level owner with the account's own as its fallback,
 * per the query param's own description) and the merchant/date/category
 * fields this panel's row actually renders.
 *
 * **`amountCents` is re-signed for display via `signedTransactionCents`**
 * (`../../api/parseMoney.ts` — a wire-format convention, not an accounts-
 * module concern, so it lives beside `parseMoney` rather than here) —
 * storage is expense-positive, so a raw `parseMoney` on `t.amount` renders a
 * grocery bill `+$118.44` in `--pos` green, an expense colouring as a gain.
 * Found live against the seeded backend (team-lead, 2026-09-17); see that
 * function's own docblock for the full chain and for why
 * `/transactions/summary`'s `net` is a DIFFERENT field that must NOT be
 * re-signed the same way.
 */
export type AccountTransaction = {
  id: string
  name: string
  date: string
  amountCents: number
  currency: string
  categoryId: string | null
  personalFinanceCategoryPrimary: string | null
  ownerName: string | null
  isPending: boolean
}

export type AccountTransactionPage = {
  transactions: AccountTransaction[]
  total: number
}

/** A preview, not a full list — there is nowhere to page TO. `/transactions`
 *  (M6) does not exist as a route yet, so this panel has no "See all"
 *  destination (#425 §B5 draws one; omitted here rather than linked to a
 *  route the router does not have, the same call `Accounts.tsx` already made
 *  for "Import statement"). Ten rows is enough to show real activity without
 *  claiming to be the whole list — the "N of Total" heading below still says
 *  so honestly when there is more. */
const PREVIEW_PAGE_SIZE = 10

async function listAccountTransactions(accountId: string): Promise<AccountTransactionPage> {
  const { data, error, response } = await api.GET('/api/v1/transactions', {
    params: { query: { account_id: accountId, page: 1, page_size: PREVIEW_PAGE_SIZE } },
  })
  if (error !== undefined || data === undefined) throw new AccountsError(error, response.status)
  return {
    transactions: data.transactions.map((t) => ({
      id: t.id,
      name: t.name,
      date: t.transaction_date,
      amountCents: signedTransactionCents(parseMoney(t.amount)),
      currency: t.iso_currency_code,
      categoryId: t.category_id ?? null,
      personalFinanceCategoryPrimary: t.personal_finance_category_primary ?? null,
      ownerName: t.effective_beneficial_owner?.display_name ?? t.effective_beneficial_owner?.email ?? null,
      isPending: t.is_pending,
    })),
    total: data.total,
  }
}

/**
 * `live` — `TransactionResponse` carries no `last_synced_at`, the same
 * reasoning `merchant-transactions` already established for the identical
 * shape (`surfaces.ts`). `isEmpty` on `total === 0`: a real account can
 * genuinely have no transactions yet (freshly linked, or manual with nothing
 * entered), which is a normal state rather than a failure.
 */
export function useAccountTransactions(accountId: string): AsyncState<AccountTransactionPage> {
  const query = useQuery({
    queryKey: keys.transactions.list({ account_id: accountId }),
    queryFn: () => listAccountTransactions(accountId),
  })
  return fromQuery(query, {
    message: () => 'Transactions could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (page) => page.total === 0,
  })
}
